import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ActionItem, ApiFragment, BaseContainer, PartialRecord, ViewerActionsPosition } from '@stylehaven/components';
import { isEqual } from 'lodash-es';

@Component({
    selector: 'med-fragment-preview',
    templateUrl: './fragment-preview.component.html',
    styleUrls: ['./fragment-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FragmentPreviewComponent extends BaseContainer implements OnInit, OnDestroy {
    public readonly ViewerActionsPosition: typeof ViewerActionsPosition = ViewerActionsPosition;
    public readonly record$: ReplaySubject<ApiFragment> = new ReplaySubject<ApiFragment>(1);

    public recordTitle$: Observable<string>;
    public recordPreviewUrl$: Observable<string>;

    public allowTransform$: Observable<boolean>;

    public customActionItems: ActionItem[];

    @Input() set record(record: PartialRecord) {
        // Currently only support ApiFragment
        if (record && record instanceof ApiFragment) {
            this.record$.next(record);
        }
    }

    @Input() allowFullscreen: boolean = true;

    @Output() overlayClick: EventEmitter<PartialRecord> = new EventEmitter<PartialRecord>();

    constructor(private readonly router: Router) {
        super();
    }

    ngOnInit(): void {
        this.createFragmentStreams();
        this.initCustomActions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.record$.complete();
    }

    public showFullscreen(): void {
        const currentUrl = this.router.url.split('(')[0]; // Get URL without outlets
        this.router.navigateByUrl(currentUrl + '/(preview:preview)');
    }

    private createFragmentStreams(): void {
        this.recordTitle$ = this.createrecordTitleStream();
        this.recordPreviewUrl$ = this.createFragmentPreviewStream();
        this.allowTransform$ = this.allowTransformStream();
    }

    private createFragmentPreviewStream(): Observable<string> {
        return this.record$.pipe(
            map((record: ApiFragment) => record.getImagePreviewUrl()),
            distinctUntilChanged(isEqual)
        );
    }

    private createrecordTitleStream(): Observable<string> {
        return this.record$.pipe(
            map((record: ApiFragment) => record.getTitle()),
            distinctUntilChanged(isEqual)
        );
    }

    private allowTransformStream(): Observable<boolean> {
        return this.record$.pipe(
            map(record => !(record.doesNotHaveBrowse() || record.hasIncompleteBrowse())),
            distinctUntilChanged()
        );
    }

    private initCustomActions(): void {
        this.customActionItems = this.allowFullscreen
            ? [
                  {
                      label: this.translateService.translate('fragments.show-bigger'),
                      icon: 'expand',
                      eventListener: () => this.showFullscreen()
                  }
              ]
            : [];
    }
}
